// Sends browser errors to Rollbar. The SDK (~32 KB gzipped) is only imported
// once there is something to report, so a visitor whose page never errors never
// downloads it. Reports go through /api/rollbar rather than straight to
// api.rollbar.com, which content blockers commonly block — the same blockers
// that tend to cause the failures most worth hearing about.

const token = import.meta.env.PUBLIC_ROLLBAR_CLIENT_TOKEN;

let rollbar = null;

// Crawlers run our scripts in half-working environments and report errors no visitor
// can hit. Case-sensitive on purpose: a case-insensitive /bot/ also catches phone
// models like "CUBOT".
const BOT_UA = /[a-z]bot\b|Bot\b|[Ss]pider|[Cc]rawl|HeadlessChrome|Lightpanda/;

// Scrapers that spoof an ordinary Chrome UA give themselves away by rebuilding the
// page without working SVG. Some still pass `instanceof SVGElement`, so test the
// geometry API itself, which every real browser has on a parsed <svg>.
const isPageRebuilt = () => {
    const svg = document.querySelector('svg');
    return svg !== null && svg.viewBox === undefined;
};

// A view transition rejects when the tab is hidden mid-navigation; the navigation
// itself still completes.
const isAbortedTransition = (reason) =>
    reason instanceof DOMException && reason.name === 'InvalidStateError' && /^Transition was aborted/.test(reason.message);

// Chunk filenames change hash every deploy and stack depth varies between sessions,
// so Rollbar's default stack-based grouping files one problem as many items. Group
// by error, message and the chunk it was thrown from, minus the hash.
const fingerprint = (data) => {
    const trace = data.body?.trace ?? data.body?.trace_chain?.[0];
    if (!trace?.exception) return;
    // When component scripts fail to load, every Alpine expression on the page
    // throws its own "x is not defined", so they're filed as one item.
    if (trace.exception.class === 'ReferenceError' && trace.frames?.at(-1)?.method?.startsWith('[Alpine]')) {
        data.fingerprint = 'Alpine: expression references an unregistered component';
        data.title = data.fingerprint;
        return;
    }
    const file = trace.frames?.at(-1)?.filename ?? '';
    const chunk = file.split('?')[0].split('/').pop().replace(/\.[\w-]{8}\.js$/, '');
    data.fingerprint = `${trace.exception.class}: ${trace.exception.message} @ ${chunk}`;
};

const load = () =>
    (rollbar ??= import('rollbar').then(
        ({ default: Rollbar }) =>
            new Rollbar({
                accessToken: token,
                endpoint: `${window.location.origin}/api/rollbar`,
                environment: __DEPLOY_ENV__,
                codeVersion: __BUILD_ID__,
                // The listeners below do this, so they can run before the SDK loads.
                captureUncaught: false,
                captureUnhandledRejections: false,
                captureIp: false,
                maxItems: 10,
                transform: fingerprint,
                payload: {
                    client: { javascript: { source_map_enabled: true, code_version: __BUILD_ID__ } },
                },
            }),
    ));

/**
 * Never throws, so it is safe in a catch block. Lead forms pass their failed
 * `submission` so the enquiry can be recovered by hand — that is the one
 * deliberate exception; keep any other personal data out of `context`.
 */
export function reportError(error, context = {}) {
    if (!token || BOT_UA.test(navigator.userAgent) || navigator.webdriver || isPageRebuilt()) return;
    load()
        .then((instance) => instance.error(error, { ...context, url: window.location.href }))
        .catch(() => {});
}

window.addEventListener('error', (event) => {
    // Cross-origin scripts (GTM, extensions) surface as a bare "Script error."
    // with no error object or stack — nothing to act on.
    if (!event.error) return;
    reportError(event.error, { uncaught: true });
});

window.addEventListener('unhandledrejection', (event) => {
    if (isAbortedTransition(event.reason)) return;
    const reason = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
    reportError(reason, { unhandledRejection: true });
});
